// PEZULA Leasing Factor Table and Calculation Logic
// All calculations happen client-side for instant results

export type TermMonths = 24 | 36 | 48 | 54 | 60

export interface NAWBand {
  min: number
  max: number
  label: string
}

export const NAW_BANDS: NAWBand[] = [
  { min: 500, max: 5000, label: '500,00 - 5.000,00 EUR' },
  { min: 5000.01, max: 15000, label: '5.000,01 - 15.000,00 EUR' },
  { min: 15000.01, max: 25000, label: '15.000,01 - 25.000,00 EUR' },
  { min: 25000.01, max: 50000, label: '25.000,01 - 50.000,00 EUR' },
  { min: 50000.01, max: 75000, label: '50.000,01 - 75.000,00 EUR' },
]

export const TERMS: TermMonths[] = [24, 36, 48, 54, 60]

// Leasing factors as percentages (from the provided table)
// Row = term, Column = NAW band index
export const LEASING_FACTORS: Record<TermMonths, number[]> = {
  24: [4.65, 4.63, 4.61, 4.59, 4.57],
  36: [3.16, 3.15, 3.14, 3.13, 3.12],
  48: [2.44, 2.43, 2.42, 2.41, 2.4],
  54: [2.22, 2.2, 2.19, 2.18, 2.17],
  60: [2.02, 2.01, 2.0, 1.99, 1.97],
}

export interface CalculationResult {
  monthlyRate: number
  factor: number
  bandIndex: number
  isValid: boolean
  errorMessage?: string
}

export interface AllTermsResult {
  term: TermMonths
  monthlyRate: number
  factor: number
}

export interface ReverseCalculationResult {
  naw: number
  factor: number
  bandIndex: number
  isValid: boolean
  errorMessage?: string
}

/**
 * Get the NAW band index for a given value
 * For NAW > 75.000, use the highest band's factors
 */
export function getNAWBandIndex(naw: number): number {
  for (let i = 0; i < NAW_BANDS.length; i++) {
    if (naw >= NAW_BANDS[i].min && naw <= NAW_BANDS[i].max) {
      return i
    }
  }
  // For NAW above 75.000, use the last band (50.000,01 - 75.000,00)
  if (naw > 75000) {
    return NAW_BANDS.length - 1
  }
  return -1
}

/**
 * Calculate the monthly leasing rate
 * Formula: monthlyRate = NAW * (factor / 100)
 */
export function calculateLeasingRate(
  naw: number,
  term: TermMonths,
): CalculationResult {
  // Validate NAW range
  if (naw < 500) {
    return {
      monthlyRate: 0,
      factor: 0,
      bandIndex: -1,
      isValid: false,
      errorMessage: 'Mindest-NAW: 500,00 EUR',
    }
  }

  const bandIndex = getNAWBandIndex(naw)
  if (bandIndex === -1) {
    return {
      monthlyRate: 0,
      factor: 0,
      bandIndex: -1,
      isValid: false,
      errorMessage: 'Ungültiger NAW-Bereich',
    }
  }

  const factor = LEASING_FACTORS[term][bandIndex]
  const monthlyRate = naw * (factor / 100)

  return {
    monthlyRate,
    factor,
    bandIndex,
    isValid: true,
  }
}

/**
 * Calculate rates for all terms at once (for expert view)
 */
export function calculateAllTerms(naw: number): AllTermsResult[] | null {
  if (naw < 500) {
    return null
  }

  const bandIndex = getNAWBandIndex(naw)
  if (bandIndex === -1) {
    return null
  }

  return TERMS.map((term) => {
    const factor = LEASING_FACTORS[term][bandIndex]
    return {
      term,
      monthlyRate: naw * (factor / 100),
      factor,
    }
  })
}

/**
 * Reverse calculation: from monthly rate to NAW
 * Formula: NAW = monthlyRate / (factor / 100)
 * We iterate bands to find the one where the calculated NAW fits.
 * For NAW > 75.000, use the highest band's factor.
 */
export function calculateNAWFromRate(
  monthlyRate: number,
  term: TermMonths,
): ReverseCalculationResult {
  if (monthlyRate <= 0) {
    return {
      naw: 0,
      factor: 0,
      bandIndex: -1,
      isValid: false,
      errorMessage: 'Bitte geben Sie eine gültige Rate ein.',
    }
  }

  const factors = LEASING_FACTORS[term]

  // Try each band: calculate NAW with that band's factor, check if it fits
  for (let i = 0; i < NAW_BANDS.length; i++) {
    const factor = factors[i]
    const calculatedNAW = monthlyRate / (factor / 100)

    if (
      calculatedNAW >= NAW_BANDS[i].min &&
      calculatedNAW <= NAW_BANDS[i].max
    ) {
      return {
        naw: calculatedNAW,
        factor,
        bandIndex: i,
        isValid: true,
      }
    }
  }

  // Check if rate is too low (NAW would be below 500)
  const lowestFactor = factors[0]
  const nawWithLowest = monthlyRate / (lowestFactor / 100)
  if (nawWithLowest < 500) {
    return {
      naw: 0,
      factor: 0,
      bandIndex: -1,
      isValid: false,
      errorMessage: 'Rate zu niedrig – Mindest-NAW: 500,00 EUR',
    }
  }

  // For NAW above 75.000, use the highest band's factor
  const highestBandIndex = NAW_BANDS.length - 1
  const highestFactor = factors[highestBandIndex]
  const nawWithHighest = monthlyRate / (highestFactor / 100)
  if (nawWithHighest > 75000) {
    return {
      naw: nawWithHighest,
      factor: highestFactor,
      bandIndex: highestBandIndex,
      isValid: true,
    }
  }

  return {
    naw: 0,
    factor: 0,
    bandIndex: -1,
    isValid: false,
    errorMessage: 'Berechnung nicht möglich.',
  }
}

/**
 * Format number as German currency
 */
export function formatCurrency(value: number): string {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)
}

/**
 * Format number with German decimal separator
 */
export function formatNumber(value: number, decimals: number = 2): string {
  return new Intl.NumberFormat('de-DE', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value)
}
